import { useState, useEffect } from "react";
import ModalFrame from "../../components/modal/ModalFrame";
import { Dialog } from "@headlessui/react";
import { ReceiptTaxIcon } from "@heroicons/react/outline";
import { useNavigate } from "react-router-dom";

import * as api from "./apiService";
import { toast } from "react-toastify";

const JobInvoiceModal = ({ isOpen, handleClose, invoiceDetails }) => {
  const navigate = useNavigate();

  const [internalAdditionalCost, setInternalAdditionalCost] = useState(0);
  const [vendorName, setVendorName] = useState(null);
  const [vendorAdditionalCost, setVendorAdditionalCost] = useState(0);
  const [vendorCharge, setVendorCharge] = useState(0);

  useEffect(() => {
    setInternalAdditionalCost(invoiceDetails.internal_additional_cost);

    if (invoiceDetails.vendor) {
      setVendorName(invoiceDetails.vendor.name);
      setVendorCharge(invoiceDetails.vendor.charge);
      setVendorAdditionalCost(invoiceDetails.vendor.additional_cost);
    }
  }, []);

  const handleInvoice = async () => {
    const request = {
      status: "I",
      internal_additional_cost: internalAdditionalCost,
      vendor_charge: vendorCharge,
      vendor_additional_cost: vendorAdditionalCost,
    };

    try {
      await api.invoiceJob(invoiceDetails.job_id, request);

      toast.success("Job invoiced!");

      navigate(0);
    } catch (err) {
      toast.error("Unable to invoice job");
    }
  };

  const handleVendorChargeChange = (e) => {
    const charge = e.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");

    setVendorCharge(charge);
  };

  const handleVendorAdditionalCostChange = (e) => {
    const cost = e.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");

    setVendorAdditionalCost(cost);
  };

  const handleInternalAdditionalCostChange = (e) => {
    const cost = e.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");

    setInternalAdditionalCost(cost);
  };

  return (
    <ModalFrame isModalOpen={isOpen}>
      <div className="job-details-redesign text-sm leading-6 text-slate-700">
        <div className="">
          <div className="flex items-center gap-3 border-b border-slate-200 pb-5">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-red-50 text-[#E3222A]">
              <ReceiptTaxIcon className="h-5 w-5" aria-hidden="true" />
            </span>
            <Dialog.Title
              as="h3"
              className="text-lg font-semibold leading-6 tracking-tight text-slate-950"
            >
              Invoice Job
            </Dialog.Title>
          </div>

          <div className="mt-5 space-y-4">
            <div className="grid grid-cols-1 items-center gap-2 sm:grid-cols-[minmax(0,1fr)_160px] sm:gap-4">
              <div>
                <label
                  for="internalAdditionalCost"
                  className="block text-sm font-medium leading-6 text-slate-700"
                >
                  Internal Additional Cost:
                </label>
              </div>
              <div className="">
                <div className="flex items-center gap-2 sm:justify-end">
                  <div className="pointer-events-none flex items-center">
                    <span className="text-sm text-slate-400">$</span>
                  </div>
                  <input
                    type="text"
                    id="internalAdditionalCost"
                    value={internalAdditionalCost}
                    onChange={(e) =>
                      handleInternalAdditionalCostChange(e.target.value)
                    }
                    className="block min-w-0 flex-1 rounded-lg border-slate-200 bg-white px-3 py-2 text-base text-slate-900 shadow-sm focus:border-slate-400 focus:ring-slate-400 sm:w-28 sm:flex-none sm:text-sm"
                  />
                  <span className="text-xs font-medium text-slate-400">
                    USD
                  </span>
                </div>
              </div>
            </div>

            {vendorName && (
              <>
                <div className="grid grid-cols-1 gap-1 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <div className="">
                    <label
                      for="vendorCharge"
                      className="block text-sm font-medium leading-6 text-slate-700"
                    >
                      Vendor:
                    </label>
                  </div>
                  <div className="">
                    <div className="break-words text-sm font-semibold text-slate-900">
                      {vendorName}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 items-center gap-2 sm:grid-cols-[minmax(0,1fr)_160px] sm:gap-4">
                  <div className="">
                    <label
                      for="vendorCharge"
                      className="block text-sm font-medium leading-6 text-slate-700"
                    >
                      Vendor Charge:
                    </label>
                  </div>
                  <div className="">
                    <div className="flex items-center gap-2 sm:justify-end">
                      <div className="pointer-events-none flex items-center">
                        <span className="text-sm text-slate-400">$</span>
                      </div>
                      <input
                        type="text"
                        id="vendorCharge"
                        value={vendorCharge}
                        onChange={(e) =>
                          handleVendorChargeChange(e.target.value)
                        }
                        className="block min-w-0 flex-1 rounded-lg border-slate-200 bg-white px-3 py-2 text-base text-slate-900 shadow-sm focus:border-slate-400 focus:ring-slate-400 sm:w-28 sm:flex-none sm:text-sm"
                      />
                      <span className="text-xs font-medium text-slate-400">
                        USD
                      </span>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 items-center gap-2 sm:grid-cols-[minmax(0,1fr)_160px] sm:gap-4">
                  <div>
                    <label
                      for="vendorAdditionalCost"
                      className="block text-sm font-medium leading-6 text-slate-700"
                    >
                      Vendor Additional Cost:
                    </label>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-2 sm:justify-end">
                      <div className="pointer-events-none flex items-center">
                        <span className="text-sm text-slate-400">$</span>
                      </div>
                      <input
                        type="text"
                        id="vendorAdditionalCost"
                        value={vendorAdditionalCost}
                        onChange={(e) =>
                          handleVendorAdditionalCostChange(e.target.value)
                        }
                        className="block min-w-0 flex-1 rounded-lg border-slate-200 bg-white px-3 py-2 text-base text-slate-900 shadow-sm focus:border-slate-400 focus:ring-slate-400 sm:w-28 sm:flex-none sm:text-sm"
                      />
                      <span className="text-xs font-medium text-slate-400">
                        USD
                      </span>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="mt-6 border-t border-slate-200 pt-5 sm:flex sm:flex-row-reverse sm:gap-3">
          <button
            type="button"
            onClick={handleInvoice}
            className="inline-flex min-h-[40px] w-full items-center justify-center rounded-lg border border-transparent
                              bg-[#E3222A] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#c91d24]
                              focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:w-auto"
          >
            Invoice
          </button>

          <button
            type="button"
            onClick={handleClose}
            className="mt-3 inline-flex min-h-[40px] w-full items-center justify-center rounded-lg border
                              border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition
                              hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 sm:mt-0 sm:w-auto"
          >
            Cancel
          </button>
        </div>
      </div>
    </ModalFrame>
  );
};

export default JobInvoiceModal;
